"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "motion/react";
import { Volume2, VolumeX, Plus } from "lucide-react";
import {
  FaInstagram,
  FaFacebookF,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import { useSliderPlayer } from "@/components/SliderPlayerContext";

/**
 * Contextual floating dock — imitates Apple's section-aware CTA pill, and
 * morphs into a video-slider transport (progress pill + play/pause) when
 * the user is in the #highlights section on the homepage.
 *
 * Per-route CTA registry: each path defines its own `section id → CTA`
 * list so the dock can live on /about / /books / etc. without dragging
 * homepage-specific labels into every page.
 */
const HOME_CTAS = [
  { id: "story", label: "Learn more about Burak", href: "/about" },
  {
    id: "journey",
    label: "Follow the journey on YouTube",
    href: "https://www.youtube.com/@burakozgurmd",
    external: true,
  },
  { id: "library", label: "Explore books written by Burak Ozgur", href: "/books" },
];

const ABOUT_CTAS = [
  {
    id: "ab-video",
    label: "Follow the journey on YouTube",
    href: "https://www.youtube.com/@burakozgurmd",
    external: true,
  },
  {
    id: "ab-awards",
    label: "Read work from Burak Ozgur",
    href: "/books",
  },
  {
    id: "ab-spans",
    kind: "social",
    label: "Follow the journey",
  },
];

const CTAS_BY_PATH = {
  "/": HOME_CTAS,
  "/about": ABOUT_CTAS,
};

// Section IDs on / that need tracking even without their own CTA
// (#highlights swaps the dock to a player transport instead).
const TRACKED_BY_PATH = {
  "/": ["story", "journey", "library", "highlights"],
  "/about": ["ab-video", "ab-awards", "ab-spans"],
};

const SOCIAL_LINKS = [
  { icon: FaInstagram, label: "Instagram", href: "https://instagram.com/burakozgurmd", color: "#E1306C" },
  { icon: FaYoutube, label: "YouTube", href: "https://www.youtube.com/@burakozgurmd", color: "#FF0000" },
  { icon: FaTiktok, label: "TikTok", href: "https://tiktok.com/@burak.ozgur.md", color: "#111" },
  { icon: FaFacebookF, label: "Facebook", href: "https://facebook.com/BurakOzgurMD", color: "#1877F2" },
];

export default function ContextualDock() {
  const pathname = usePathname();
  const [activeId, setActiveId] = useState(null);
  const player = useSliderPlayer();

  const ctas = CTAS_BY_PATH[pathname] || null;
  const trackedIds = TRACKED_BY_PATH[pathname] || null;
  const ctaById = ctas
    ? Object.fromEntries(ctas.map((c) => [c.id, c]))
    : {};

  // Hard-reset on every route change so an exiting AnimatePresence child
  // from the previous page never tries to reconcile against a new page's
  // DOM. That race is the most common source of "removeChild" errors
  // when navigating away from a long-scrolled page.
  useEffect(() => {
    setActiveId(null);
  }, [pathname]);

  // Scroll-based active detection — keyed to pathname so the listener is
  // torn down + rebuilt whenever the route changes (avoids stale scroll
  // handlers firing against the new page's DOM).
  useEffect(() => {
    if (!trackedIds || trackedIds.length === 0) return undefined;

    let cancelled = false;

    const pick = () => {
      if (cancelled) return;
      const els = trackedIds
        .map((id) => ({ id, el: document.getElementById(id) }))
        .filter((x) => x.el);
      if (els.length === 0) {
        setActiveId(null);
        return;
      }
      const vh = window.innerHeight;
      const probeY = vh * 0.85;
      let next = null;
      let handoffToNext = false;
      for (const { id, el } of els) {
        const r = el.getBoundingClientRect();
        const covers = r.top <= probeY && r.bottom >= probeY;
        const dismissed = el.dataset.ctxActive === "false";
        if (dismissed) {
          if (covers) handoffToNext = true;
          continue;
        }
        if (handoffToNext) {
          next = id;
          handoffToNext = false;
          continue;
        }
        if (covers) next = id;
      }
      setActiveId(next);
    };

    pick();
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        pick();
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", pick);
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", pick);
    };
  }, [pathname, trackedIds]);

  // Highlights player mode — homepage only.
  const playerMode =
    pathname === "/" && activeId === "highlights" && player?.mounted;
  const cta = !playerMode && activeId ? ctaById[activeId] : null;

  const handleCtaClick = (e) => {
    if (!cta) return;
    if (cta.action === "openContact") {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("bo:open-contact"));
    }
  };

  const linkProps = cta?.external
    ? { href: cta.href, target: "_blank", rel: "noopener noreferrer" }
    : { href: cta?.href || "#" };

  return (
    <div className="ctx-dock" aria-hidden={!cta && !playerMode}>
      <AnimatePresence mode="wait" initial={false}>
        {playerMode ? (
          <motion.div
            key="player"
            className="ctx-dock-shell ctx-dock-shell--player"
            initial={{ scale: 0.7, opacity: 0, y: 16, filter: "blur(10px)" }}
            animate={{ scale: 1, opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ scale: 0.9, opacity: 0, y: 10, filter: "blur(8px)" }}
            transition={{
              opacity: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
              filter: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
              scale: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
              y: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
            }}
          >
            <PlayerControls />
          </motion.div>
        ) : (
          cta && (
            <motion.div
              key={`${pathname}:${cta.id}`}
              className={`ctx-dock-shell${cta.kind === "social" ? " ctx-dock-shell--social" : ""}`}
              initial={{ scale: 0.7, opacity: 0, y: 16, filter: "blur(10px)" }}
              animate={{ scale: 1, opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ scale: 0.9, opacity: 0, y: 10, filter: "blur(8px)" }}
              transition={{
                opacity: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                filter: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                scale: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
                y: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
              }}
            >
              {cta.kind === "social" ? (
                <div className="ctx-dock-pill ctx-dock-pill--social">
                  <span className="ctx-dock-label">{cta.label}</span>
                  <span className="ctx-dock-socials" aria-hidden="false">
                    {SOCIAL_LINKS.map(({ icon: Icon, label, href, color }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ctx-dock-social-btn"
                        aria-label={label}
                      >
                        <Icon size={13} style={{ color }} />
                      </a>
                    ))}
                  </span>
                </div>
              ) : (
                <a {...linkProps} className="ctx-dock-pill" onClick={handleCtaClick}>
                  <span className="ctx-dock-label">{cta.label}</span>
                  <span className="ctx-dock-icon" aria-hidden="true">
                    <Plus size={18} strokeWidth={2.6} />
                  </span>
                </a>
              )}
            </motion.div>
          )
        )}
      </AnimatePresence>
    </div>
  );
}

function PlayerControls() {
  const { slides, activeIndex, muted, progress, toggleMute, goTo } = useSliderPlayer();
  if (!slides || slides.length === 0) return null;

  return (
    <div className="ctx-player" role="group" aria-label="Highlight slider controls">
      <div className="ctx-player-track">
        {slides.map((_, i) => {
          const isActive = i === activeIndex;
          return (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              className={`ctx-player-tick${isActive ? " is-active" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={isActive}
            >
              {isActive ? (
                <span
                  className="ctx-player-fill"
                  style={{ width: `${Math.max(6, progress * 100)}%` }}
                />
              ) : null}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="ctx-player-playpause"
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
      >
        {muted ? (
          <VolumeX size={18} strokeWidth={2.2} />
        ) : (
          <Volume2 size={18} strokeWidth={2.2} />
        )}
      </button>
    </div>
  );
}
